"use client";

import { useCallback, useEffect } from "react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { getAppEnvKind } from "@/shared/lib/app-env";
import { isTauri } from "@/shared/lib/tauri";

const DRAG_REGION = { "data-tauri-drag-region": true as const };

function getTitleSuffix(): string {
  const env = getAppEnvKind();
  if (env === "local") return " dev";
  if (env === "test") return " test";
  return "";
}

function MinIcon() {
  return (
    <svg width="10" height="1" viewBox="0 0 10 1" fill="currentColor">
      <rect width="10" height="1" />
    </svg>
  );
}

function MaxIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1">
      <rect x="0.5" y="0.5" width="9" height="9" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.2">
      <path d="M1 1l8 8M9 1L1 9" />
    </svg>
  );
}

export function TauriTitleBar() {
  const tUi = useTranslations("Ui");
  const handleMinimize = useCallback(async () => {
    if (!isTauri) return;
    const { getCurrentWindow } = await import("@tauri-apps/api/window");
    await getCurrentWindow().minimize();
  }, []);

  const handleMaximize = useCallback(async () => {
    if (!isTauri) return;
    const { getCurrentWindow } = await import("@tauri-apps/api/window");
    await getCurrentWindow().toggleMaximize();
  }, []);

  const handleClose = useCallback(async () => {
    if (!isTauri) return;
    const { getCurrentWindow } = await import("@tauri-apps/api/window");
    await getCurrentWindow().close();
  }, []);

  const titleSuffix = getTitleSuffix();

  useEffect(() => {
    if (!isTauri || !titleSuffix) return;
    const title = `GoodLuck${titleSuffix}`;
    import("@tauri-apps/api/window").then(({ getCurrentWindow }) =>
      getCurrentWindow().setTitle(title),
    );
  }, [titleSuffix]);

  if (!isTauri) return null;

  return (
    <header className="tauri-title-bar flex h-11 flex-shrink-0 items-stretch border-b border-border bg-background px-0 select-none">
      <div className="grid h-full w-full grid-cols-3 items-stretch gap-0">
        <div className="flex h-full min-w-0 items-center gap-2 pl-3" {...DRAG_REGION}>
          <Image
            src="/icons/icon-192x192.png"
            alt=""
            width={24}
            height={24}
            className="h-6 w-6 flex-shrink-0 select-none"
            draggable={false}
          />
        </div>
        <div className="flex h-full items-center justify-center" {...DRAG_REGION}>
          <span className="text-sm font-bold text-foreground">
            GoodLuck{titleSuffix}
          </span>
        </div>
        <div className="flex h-full min-w-0">
          <div className="h-full min-w-0 flex-1" {...DRAG_REGION} />
          <div className="flex h-full flex-shrink-0 items-stretch">
            <button
              type="button"
              onClick={handleMinimize}
              className="flex h-full w-12 flex-shrink-0 items-center justify-center text-foreground/80 hover:bg-muted hover:text-foreground"
              aria-label={tUi("tauriTitleBar.svernut")}
            >
              <MinIcon />
            </button>
            <button
              type="button"
              onClick={handleMaximize}
              className="flex h-full w-12 flex-shrink-0 items-center justify-center text-foreground/80 hover:bg-muted hover:text-foreground"
              aria-label={tUi("tauriTitleBar.razvernut")}
            >
              <MaxIcon />
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="flex h-full w-12 flex-shrink-0 items-center justify-center text-foreground/80 hover:bg-destructive hover:text-destructive-foreground"
              aria-label={tUi("tauriTitleBar.zakryt")}
            >
              <CloseIcon />
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}
